import { CircleHelpIcon, InfoIcon } from "lucide-react";
import { useState } from "react";

import { Popover, PopoverPopup, PopoverTrigger } from "~/components/ui/popover";

import {
  createUnavailableAiEngineeringSnapshot,
  metricStateLabel,
  type AiEngineeringBreakoutRow,
  type AiEngineeringMetric,
  type AiEngineeringMetricState,
  type AiEngineeringOutcomeCohort,
  type AiEngineeringReviewWorkflow,
  type AiEngineeringSnapshotResponse,
  type AiEngineeringTrendPoint,
} from "./aiEngineeringTypes";
import {
  buildReviewWorkflowGraph,
  type ReviewWorkflowGraphLink,
  type ReviewWorkflowGraphNode,
} from "./aiEngineeringReviewFlow";
import { ChartPanel, DataTable, FilterToggle } from "./dashboardKit";

const METHODS_DOC_URL =
  "https://github.com/Craniometrix/operators/blob/main/docs/ai-engineering-metrics-methods.qmd";

const COMPARISON_DAYS = 7;

/*
 * Visual language, in one place. Each palette colour has exactly one job:
 *
 *   Pacific cyan   accent - the current period on sparklines, the emphasised
 *                  branch in the review flow, the header mark.
 *   Raspberry plum identity - the AI-assisted cohort wherever it is compared
 *                  with "other" (which is always gray).
 *   Emerald        good / measured - improved deltas, the measured pill.
 *   Sandy brown    attention - worsened deltas, coverage gaps, partial data.
 *   Banana cream   insufficient data.
 *
 * Emerald, sandy brown, and banana cream do not clear text contrast on either
 * surface, so they only ever appear as fills behind dark ink or as marks beside
 * text set in text tokens. Everything not carrying one of these jobs is gray.
 */
const PALETTE = {
  bananaCream: "#efea5a",
  emerald: "#16db93",
  pacificCyan: "#048ba8",
  raspberryPlum: "#a4036f",
  sandyBrown: "#f29e4c",
} as const;
const ACCENT_CLASS = "stroke-[#048ba8]";
const ACCENT_FILL_CLASS = "fill-[#048ba8]";
const AI_COHORT_FILL_CLASS = "fill-[#a4036f]";
const ATTENTION_STROKE_CLASS = "stroke-[#f29e4c]";
const ATTENTION_FILL_CLASS = "fill-[#f29e4c]";
const CONTEXT_STROKE_CLASS = "stroke-muted-foreground";

function stateClassName(state: AiEngineeringMetricState): string {
  if (state === "measured") {
    return "border-[#16db93]/60 bg-[#16db93]/25 text-[#0b4f3a] dark:bg-[#16db93]/20 dark:text-[#b9f5de]";
  }
  if (state === "insufficient_data") {
    return "border-[#efea5a]/70 bg-[#efea5a]/45 text-[#5a5100] dark:bg-[#efea5a]/20 dark:text-[#f3ef9a]";
  }
  return "border-border bg-muted text-muted-foreground";
}

function StatePill({ state }: { state: AiEngineeringMetricState }) {
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium ${stateClassName(state)}`}
    >
      {metricStateLabel(state)}
    </span>
  );
}

/** Shown only when a value is not plainly measured; a pill on every card is noise. */
function StateFlag({ state }: { state: AiEngineeringMetricState }) {
  return state === "measured" ? null : <StatePill state={state} />;
}

function CoveragePill({ status }: { status: AiEngineeringSnapshotResponse["status"] }) {
  if (status === "partial") {
    return (
      <span className="inline-flex items-center rounded-full border border-[#f29e4c]/70 bg-[#f29e4c]/35 px-2 py-0.5 text-[11px] font-medium text-[#5c3200] dark:bg-[#f29e4c]/20 dark:text-[#fbd7b3]">
        Partial coverage
      </span>
    );
  }
  return status === "unavailable" ? <StatePill state="unavailable" /> : null;
}

function DefinitionTrigger(props: { definition: string; label: string; className?: string }) {
  return (
    <Popover>
      <PopoverTrigger
        type="button"
        aria-label={`About ${props.label}`}
        className={
          props.className ??
          "inline-flex size-4 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors hover:text-foreground"
        }
      >
        <CircleHelpIcon className="size-3.5" aria-hidden />
      </PopoverTrigger>
      <PopoverPopup side="top" tooltipStyle className="max-w-80 text-xs leading-relaxed">
        {props.definition}
      </PopoverPopup>
    </Popover>
  );
}

function SectionLabel(props: { children: string; description?: string }) {
  return (
    <div className="mt-2 flex flex-wrap items-baseline gap-x-3 gap-y-1 px-1">
      <h2 className="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
        {props.children}
      </h2>
      {props.description ? (
        <p className="text-xs text-muted-foreground">{props.description}</p>
      ) : null}
    </div>
  );
}

function percent(value: number | null): string {
  return value == null ? "—" : `${Math.round(value * 100)}%`;
}

function ratioValue(value: number | null): string {
  return value == null ? "—" : `${value.toFixed(1)}×`;
}

function currency(value: number | null): string {
  if (value == null) return "—";
  if (Math.abs(value) < 10) return `$${value.toFixed(2)}`;
  return `$${Math.round(value).toLocaleString()}`;
}

function hours(value: number | null): string {
  if (value == null) return "—";
  if (value >= 48) return `${(value / 24).toFixed(1)} d`;
  return `${value.toFixed(1)} h`;
}

function plain(value: number | null, digits = 0): string {
  return value == null ? "—" : value.toLocaleString("en-US", { maximumFractionDigits: digits });
}

function observedCount(value: number, state: AiEngineeringMetricState): string {
  return state === "unavailable" ? "—" : value.toLocaleString();
}

export function compactNumber(value: number): string {
  return new Intl.NumberFormat("en-US", {
    maximumFractionDigits: value >= 1_000_000_000 ? 2 : 1,
    notation: "compact",
  }).format(value);
}

function freshnessLabel(value: "live" | "partial" | "unavailable"): string {
  if (value === "live") return "live";
  if (value === "partial") return "partially available";
  return "unavailable";
}

function metricFootnote(
  metric: AiEngineeringMetric,
  numeratorLabel: string,
  denominatorLabel: string,
): string {
  if (metric.state === "unavailable") return "Data source unavailable";
  if (metric.state === "insufficient_data") return "No eligible observations yet";
  return `${metric.numerator.toLocaleString()} ${numeratorLabel} / ${metric.denominator.toLocaleString()} ${denominatorLabel}`;
}

type TrendMetricKey =
  | "adoption"
  | "costPerAiMergedPr"
  | "efficiency"
  | "findingsActedOn"
  | "reviewCoverage"
  | "sessionConversion"
  | "standardsCompliance"
  | "taskCompletion";

type SignalFormat = "currency" | "rate" | "ratio";
type SignalDirection = "down" | "neutral" | "up";

export function aggregateTrendMetric(
  points: AiEngineeringTrendPoint[],
  key: TrendMetricKey,
): AiEngineeringMetric {
  let numerator = 0;
  let denominator = 0;
  for (const point of points) {
    numerator += point[key].numerator;
    denominator += point[key].denominator;
  }
  if (denominator === 0) {
    return { denominator, numerator, state: "insufficient_data", value: null };
  }
  return { denominator, numerator, state: "measured", value: numerator / denominator };
}

export function recentComparison(
  points: AiEngineeringTrendPoint[],
  key: TrendMetricKey,
): { delta: number | null; prior: AiEngineeringMetric; recent: AiEngineeringMetric } {
  const recent = aggregateTrendMetric(points.slice(-COMPARISON_DAYS), key);
  const prior = aggregateTrendMetric(points.slice(-COMPARISON_DAYS * 2, -COMPARISON_DAYS), key);
  const delta = recent.value !== null && prior.value !== null ? recent.value - prior.value : null;
  return { delta, prior, recent };
}

function formatSignalValue(format: SignalFormat, value: number): string {
  if (format === "currency") return currency(value);
  if (format === "ratio") return ratioValue(value);
  return percent(value);
}

// Returns null when the change rounds to zero at display precision.
function deltaLabel(format: SignalFormat, delta: number): string | null {
  const sign = delta > 0 ? "+" : "−";
  if (format === "ratio") {
    const rounded = Math.round(Math.abs(delta) * 10) / 10;
    return rounded === 0 ? null : `${sign}${rounded.toFixed(1)}×`;
  }
  if (format === "currency") {
    const rounded = Math.round(Math.abs(delta));
    return rounded === 0 ? null : `${sign}$${rounded.toLocaleString()}`;
  }
  const points = Math.round(Math.abs(delta) * 100);
  return points === 0 ? null : `${sign}${points} pt${points === 1 ? "" : "s"}`;
}

function trendDateLabel(value: string | undefined): string {
  if (!value) return "No observations";
  const timestamp = Date.parse(`${value}T00:00:00.000Z`);
  if (!Number.isFinite(timestamp)) return value;
  return new Intl.DateTimeFormat("en-US", {
    day: "numeric",
    month: "short",
    timeZone: "UTC",
  }).format(timestamp);
}

/**
 * Stat-tile sparkline: the whole window in gray, the current comparison period
 * in the accent, one end marker with a surface ring, and oversized invisible hit
 * targets so hover and focus never need pixel accuracy.
 */
function TrendSparkline(props: {
  format: SignalFormat;
  label: string;
  metricKey: TrendMetricKey;
  points: AiEngineeringTrendPoint[];
}) {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const isRate = props.format === "rate";
  const values = props.points.map((point) => point[props.metricKey].value);
  const observed = values.filter((value): value is number => value !== null);
  const minimum = isRate && observed.length ? 0 : observed.length ? Math.min(...observed) : 0;
  const maximum = isRate && observed.length ? 1 : observed.length ? Math.max(...observed) : 1;
  const spread = maximum - minimum || Math.max(Math.abs(maximum) * 0.12, 0.1);
  const coordinates = values.map((value, index) =>
    value === null
      ? null
      : {
          x: 6 + (index / Math.max(values.length - 1, 1)) * 228,
          y: 46 - ((value - minimum) / spread) * 36,
        },
  );
  const recentStart = Math.max(values.length - COMPARISON_DAYS, 0);
  const pathFor = (from: number, to: number): string[] => {
    const segments: string[] = [];
    let segment: string[] = [];
    for (let index = from; index < to; index += 1) {
      const coordinate = coordinates[index];
      if (!coordinate) {
        if (segment.length) segments.push(segment.join(" "));
        segment = [];
        continue;
      }
      segment.push(
        `${segment.length ? "L" : "M"} ${coordinate.x.toFixed(1)} ${coordinate.y.toFixed(1)}`,
      );
    }
    if (segment.length) segments.push(segment.join(" "));
    return segments;
  };
  // Overlap by one point so the accent segment joins the gray one.
  const contextSegments = pathFor(0, Math.min(recentStart + 1, values.length));
  const recentSegments = pathFor(recentStart, values.length);
  const lastObservedIndex = coordinates.findLastIndex((coordinate) => coordinate !== null);
  const activeCoordinate = activeIndex == null ? null : coordinates[activeIndex];
  const activeValue = activeIndex == null ? null : values[activeIndex];
  const activePoint = activeIndex == null ? null : props.points[activeIndex];
  const activeLabel =
    activeCoordinate && activeValue !== null && activeValue !== undefined && activePoint
      ? `${trendDateLabel(activePoint.date)} · ${formatSignalValue(props.format, activeValue)}`
      : null;
  const tooltipWidth = 76;
  const tooltipX = activeCoordinate
    ? Math.min(Math.max(activeCoordinate.x - tooltipWidth / 2, 4), 240 - tooltipWidth - 4)
    : 0;
  const tooltipY = activeCoordinate
    ? Math.min(
        Math.max(activeCoordinate.y < 25 ? activeCoordinate.y + 8 : activeCoordinate.y - 24, 0),
        36,
      )
    : 0;

  return (
    <div>
      <svg
        aria-label={`${props.label} daily trend`}
        className="h-16 w-full overflow-visible"
        role="img"
        viewBox="0 0 240 56"
      >
        <title>{`${props.label} daily trend; ${observed.length} observed days`}</title>
        <path className="stroke-border" d="M 6 46 H 234" fill="none" strokeWidth="1" />
        {isRate ? (
          <path className="stroke-border" d="M 6 10 H 234" fill="none" strokeWidth="1" />
        ) : null}
        {contextSegments.map((path) => (
          <path
            key={`context-${path}`}
            className={CONTEXT_STROKE_CLASS}
            d={path}
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeOpacity="0.55"
            strokeWidth="2"
          />
        ))}
        {recentSegments.map((path) => (
          <path
            key={`recent-${path}`}
            className={ACCENT_CLASS}
            d={path}
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
          />
        ))}
        {coordinates.map((coordinate, index) => {
          const value = values[index];
          const point = props.points[index];
          if (!coordinate || value === null || value === undefined || !point) return null;
          const isActive = activeIndex === index;
          const isEnd = index === lastObservedIndex;
          const pointLabel = `${props.label} on ${trendDateLabel(point.date)}: ${formatSignalValue(props.format, value)}`;
          return (
            <g key={`${props.metricKey}-${point.date}`}>
              {isActive || isEnd ? (
                <circle
                  className={`${index >= recentStart ? ACCENT_FILL_CLASS : "fill-muted-foreground"} stroke-card`}
                  cx={coordinate.x}
                  cy={coordinate.y}
                  pointerEvents="none"
                  r={4}
                  strokeWidth="2"
                />
              ) : null}
              <circle
                aria-label={pointLabel}
                className="cursor-crosshair fill-transparent focus-visible:outline-none"
                cx={coordinate.x}
                cy={coordinate.y}
                onBlur={() => setActiveIndex(null)}
                onFocus={() => setActiveIndex(index)}
                onKeyDown={(event) => {
                  if (event.key === "Escape") setActiveIndex(null);
                }}
                onMouseEnter={() => setActiveIndex(index)}
                onMouseLeave={() => setActiveIndex(null)}
                r={12}
                role="button"
                tabIndex={0}
              >
                <title>{pointLabel}</title>
              </circle>
            </g>
          );
        })}
        {activeLabel && activeCoordinate ? (
          <g pointerEvents="none">
            <line
              className="stroke-muted-foreground"
              strokeOpacity="0.5"
              strokeWidth="1"
              x1={activeCoordinate.x}
              x2={activeCoordinate.x}
              y1={6}
              y2={46}
            />
            <rect
              className="fill-foreground"
              height="18"
              rx="4"
              width={tooltipWidth}
              x={tooltipX}
              y={tooltipY}
            />
            <text
              className="fill-background"
              fontSize="8"
              fontWeight="600"
              textAnchor="middle"
              x={tooltipX + tooltipWidth / 2}
              y={tooltipY + 12}
            >
              {activeLabel}
            </text>
          </g>
        ) : null}
      </svg>
      <div aria-live="polite" className="sr-only">
        {activeLabel ?? ""}
      </div>
      <div className="flex justify-between px-1 text-[10px] text-muted-foreground">
        <span>{trendDateLabel(props.points[0]?.date)}</span>
        <span>{props.points.at(-1) ? trendDateLabel(props.points.at(-1)?.date) : ""}</span>
      </div>
    </div>
  );
}

type SignalDefinition = {
  definition: string;
  denominatorLabel: string;
  direction: SignalDirection;
  footnote?: string;
  format: SignalFormat;
  key: TrendMetricKey;
  metric: AiEngineeringMetric;
  numeratorLabel: string;
  title: string;
};

function SignalCard(props: {
  compact?: boolean;
  period: string;
  points: AiEngineeringTrendPoint[];
  signal: SignalDefinition;
}) {
  const { signal } = props;
  const comparison = recentComparison(props.points, signal.key);
  const label = comparison.delta === null ? null : deltaLabel(signal.format, comparison.delta);
  const improved =
    comparison.delta === null || signal.direction === "neutral"
      ? null
      : signal.direction === "up"
        ? comparison.delta > 0
        : comparison.delta < 0;
  const deltaMark =
    label === null || improved === null ? null : improved ? PALETTE.emerald : PALETTE.sandyBrown;
  const deltaClassName =
    label === null || improved === null ? "text-muted-foreground" : "text-foreground";
  const deltaText =
    comparison.delta === null
      ? `No ${COMPARISON_DAYS}-day comparison yet`
      : label === null
        ? `No change vs prior ${COMPARISON_DAYS} days`
        : `${label} vs prior ${COMPARISON_DAYS} days`;
  return (
    <article
      className={`rounded-lg border border-border/70 bg-card text-card-foreground shadow-xs ${props.compact ? "px-3 py-3" : "px-4 py-4"}`}
    >
      <div className="flex items-start justify-between gap-2">
        <p className="inline-flex items-center gap-1 text-xs text-muted-foreground">
          {signal.title}
          <DefinitionTrigger
            definition={`${signal.definition} The headline is the ${props.period.toLowerCase()} aggregate; the accented part of the trend is the last ${COMPARISON_DAYS} days. The ${COMPARISON_DAYS}-day comparison sums daily aggregates, so a PR or session active on several days counts once per day.`}
            label={signal.title}
          />
        </p>
        <StateFlag state={signal.metric.state} />
      </div>
      <p
        className={`mt-1 font-semibold leading-none tracking-tight text-foreground ${props.compact ? "text-2xl" : "text-[2.5rem]"}`}
      >
        {signal.metric.value === null ? "—" : formatSignalValue(signal.format, signal.metric.value)}
      </p>
      <p
        className={`mt-1.5 inline-flex items-center gap-1.5 text-xs font-medium ${deltaClassName}`}
      >
        {deltaMark ? (
          <span
            aria-hidden
            className="size-2 shrink-0 rounded-full"
            data-delta={improved ? "improved" : "worsened"}
            style={{ backgroundColor: deltaMark }}
          />
        ) : null}
        <span>{deltaText}</span>
        {deltaMark ? (
          <span className="sr-only">{improved ? "(improved)" : "(worsened)"}</span>
        ) : null}
      </p>
      <div className={props.compact ? "mt-2" : "mt-3"}>
        <TrendSparkline
          format={signal.format}
          label={signal.title}
          metricKey={signal.key}
          points={props.points}
        />
      </div>
      <p className="mt-2 text-[11px] leading-relaxed text-muted-foreground">
        {signal.metric.state === "measured" && signal.footnote
          ? signal.footnote
          : metricFootnote(signal.metric, signal.numeratorLabel, signal.denominatorLabel)}
        {comparison.recent.value !== null
          ? ` · last ${COMPARISON_DAYS} days ${formatSignalValue(signal.format, comparison.recent.value)}`
          : ""}
      </p>
    </article>
  );
}

function outcomeState(
  data: AiEngineeringSnapshotResponse,
  value: number | null,
): AiEngineeringMetricState {
  if (data.outcomes.availability === "unavailable") return "unavailable";
  return value === null ? "insufficient_data" : "measured";
}

function headlineSignals(data: AiEngineeringSnapshotResponse): SignalDefinition[] {
  const { outcomes } = data;
  const costMetric: AiEngineeringMetric = {
    denominator: outcomes.mergedPrsWithAi,
    numerator: data.usage.costUsd ?? 0,
    state: outcomeState(data, outcomes.costPerAiMergedPr),
    value: outcomes.costPerAiMergedPr,
  };
  return [
    {
      definition:
        "Merged PRs with AI authoring provenance divided by all merged PRs in the tracked repositories. Provenance is the automation identity as PR author or the Operators chat link in the PR body; nothing is inferred from code style.",
      denominatorLabel: "merged PRs",
      direction: "neutral",
      format: "rate",
      key: "adoption",
      metric: outcomes.adoption,
      numeratorLabel: "AI-assisted",
      title: "AI-assisted merges",
    },
    {
      definition:
        "User sessions with AI activity whose id appears in a PR body, divided by user sessions. Sessions the automation spawns for review are excluded. A low value means sessions that never produce a reviewable change.",
      denominatorLabel: "user sessions",
      direction: "up",
      format: "rate",
      key: "sessionConversion",
      metric: outcomes.sessionConversion,
      numeratorLabel: "linked from a PR",
      title: "Sessions reaching a PR",
    },
    {
      definition:
        "Merged PRs that received a commit after their first tagged AI review finding, divided by merged PRs with at least one finding. A proxy for findings being acted on; it is not adjudicated validity.",
      denominatorLabel: "merged PRs with findings",
      direction: "up",
      format: "rate",
      key: "findingsActedOn",
      metric: outcomes.aiReview.changedAfterFinding,
      numeratorLabel: "changed after a finding",
      title: "AI findings acted on",
    },
    {
      definition:
        "Recorded inference cost divided by AI-assisted merged PRs. Cost is only what usage events recorded; it is not reconstructed from token counts.",
      denominatorLabel: "AI-assisted merged PRs",
      direction: "down",
      footnote: `${currency(data.usage.costUsd)} recorded cost / ${outcomes.mergedPrsWithAi.toLocaleString()} AI-assisted merged PRs`,
      format: "currency",
      key: "costPerAiMergedPr",
      metric: costMetric,
      numeratorLabel: "recorded cost",
      title: "Cost per AI-assisted merge",
    },
  ];
}

function instrumentSignals(data: AiEngineeringSnapshotResponse): SignalDefinition[] {
  return [
    {
      definition:
        "Successful completed AI turns divided by completed AI turns. A tooling health signal: it says the turn ended cleanly, not that the task was done.",
      denominatorLabel: "completed turns",
      direction: "up",
      format: "rate",
      key: "taskCompletion",
      metric: data.summary.taskCompletion,
      numeratorLabel: "successful turns",
      title: "Turn success",
    },
    {
      definition:
        "AI-associated PRs with an observed review request divided by AI-associated PRs seen in session or review telemetry. Measures whether the review automation fired, not whether it helped.",
      denominatorLabel: "eligible PRs",
      direction: "up",
      format: "rate",
      key: "reviewCoverage",
      metric: data.summary.reviewCoverage,
      numeratorLabel: "reviewed PRs",
      title: "Review coverage",
    },
    {
      definition:
        "Passing observed standards checks divided by checks with known pass/fail outcomes. Failures during a session are often healthy iteration, so the direction is not a quality verdict.",
      denominatorLabel: "eligible checks",
      direction: "neutral",
      format: "rate",
      key: "standardsCompliance",
      metric: data.summary.standardsCompliance,
      numeratorLabel: "passing checks",
      title: "Check pass rate",
    },
    {
      definition:
        "Completed AI turns divided by sessions with observed AI activity. Flow density only; higher is not automatically better.",
      denominatorLabel: "sessions",
      direction: "neutral",
      format: "ratio",
      key: "efficiency",
      metric: data.summary.efficiency,
      numeratorLabel: "completed turns",
      title: "Turns per session",
    },
  ];
}

function PageHeader({ data }: { data: AiEngineeringSnapshotResponse }) {
  const updated =
    data.status === "unavailable"
      ? "—"
      : data.generatedAt.replace("T", " ").replace(/\.\d{3}Z$/, " UTC");
  return (
    <header className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
        <span className="inline-flex items-center gap-1.5">
          <InfoIcon
            className="size-3.5 shrink-0"
            style={{ color: PALETTE.pacificCyan }}
            aria-hidden
          />
          {data.window.since && data.window.until
            ? `${data.window.label} (${data.window.since} → ${data.window.until})`
            : data.window.label}
        </span>
        <span aria-hidden>·</span>
        <span>Operators {freshnessLabel(data.freshness.operators)}</span>
        <span aria-hidden>·</span>
        <span>GitHub {freshnessLabel(data.freshness.github)}</span>
        <span aria-hidden>·</span>
        <span>PR outcomes {freshnessLabel(data.outcomes.availability)}</span>
        <span aria-hidden>·</span>
        <span>
          Models: {data.provenance.models.length ? data.provenance.models.join(", ") : "—"}
        </span>
        <span aria-hidden>·</span>
        <span>Updated {updated}</span>
        <CoveragePill status={data.status} />
      </div>
      <div>
        <h2 className="text-lg font-semibold tracking-tight text-foreground">
          Is AI making us more productive, and is AI review working?
        </h2>
        <p className="mt-1 max-w-3xl text-sm leading-relaxed text-muted-foreground">
          Headlines are full-window aggregates of merged PRs, sessions, review findings, and
          recorded cost. Everything is observational: engineers choose when to use AI, so
          differences describe the work that was done, not a causal effect.
        </p>
      </div>
    </header>
  );
}

function HeadlineRow({ data }: { data: AiEngineeringSnapshotResponse }) {
  return (
    <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
      {headlineSignals(data).map((signal) => (
        <SignalCard
          key={signal.key}
          period={data.window.label}
          points={data.trends}
          signal={signal}
        />
      ))}
    </div>
  );
}

type ComparisonRow = {
  definition: string;
  format: (cohort: AiEngineeringOutcomeCohort) => string;
  gap: (ai: AiEngineeringOutcomeCohort, other: AiEngineeringOutcomeCohort) => string;
  metric: string;
};

function signedNumber(value: number, format: (value: number) => string): string {
  if (value === 0) return "0";
  return `${value > 0 ? "+" : "−"}${format(Math.abs(value))}`;
}

function gapOf(
  left: number | null,
  right: number | null,
  format: (value: number) => string,
): string {
  if (left == null || right == null) return "—";
  return signedNumber(left - right, format);
}

function gapOfShare(left: AiEngineeringMetric, right: AiEngineeringMetric): string {
  if (left.value == null || right.value == null) return "—";
  const points = Math.round((left.value - right.value) * 100);
  return points === 0 ? "0 pts" : `${points > 0 ? "+" : "−"}${Math.abs(points)} pts`;
}

function ProductivityPanel({ data }: { data: AiEngineeringSnapshotResponse }) {
  const cohorts = data.outcomes.cohorts;
  const byName = new Map(cohorts.map((cohort) => [cohort.cohort, cohort]));
  const unavailable = data.outcomes.availability === "unavailable";
  const rows: ComparisonRow[] = [
    {
      definition:
        "Merged PRs in the window. AI-assisted means the automation identity authored the PR or the body carries an Operators chat link.",
      format: (cohort) => (unavailable ? "—" : cohort.mergedPrs.toLocaleString()),
      gap: () => "",
      metric: "Merged PRs",
    },
    {
      definition:
        "Median of PR created-at to merged-at. Includes waiting time, so it is delivery latency rather than effort.",
      format: (cohort) => hours(cohort.medianHoursToMerge),
      gap: (ai, other) =>
        gapOf(ai.medianHoursToMerge, other.medianHoursToMerge, (value) => hours(value)),
      metric: "Median time to merge",
    },
    {
      definition:
        "Median additions plus deletions. Shown so cohorts are not compared as if their PRs were the same size.",
      format: (cohort) => plain(cohort.medianLinesChanged),
      gap: (ai, other) =>
        gapOf(ai.medianLinesChanged, other.medianLinesChanged, (value) => plain(value)),
      metric: "Median lines changed",
    },
    {
      definition:
        "Median human (non-automation) review submissions per PR: approvals, change requests, and review comments.",
      format: (cohort) => plain(cohort.medianHumanReviews, 1),
      gap: (ai, other) =>
        gapOf(ai.medianHumanReviews, other.medianHumanReviews, (value) => plain(value, 1)),
      metric: "Median human reviews",
    },
    {
      definition: "Median human issue comments per PR in the window. A reviewer-burden signal.",
      format: (cohort) => plain(cohort.medianHumanComments, 1),
      gap: (ai, other) =>
        gapOf(ai.medianHumanComments, other.medianHumanComments, (value) => plain(value, 1)),
      metric: "Median human comments",
    },
    {
      definition: "Share of merged PRs where a human reviewer requested changes at least once.",
      format: (cohort) => percent(cohort.changesRequestedShare.value),
      gap: (ai, other) => gapOfShare(ai.changesRequestedShare, other.changesRequestedShare),
      metric: "Human changes requested",
    },
    {
      definition:
        "Share of PRs with a human review that received at least one commit after the first human review. Denominator is PRs with a human review.",
      format: (cohort) => percent(cohort.reworkShare.value),
      gap: (ai, other) => gapOfShare(ai.reworkShare, other.reworkShare),
      metric: "Rework after first review",
    },
    {
      definition:
        "Share of merged PRs undone by a revert PR merged in the same window. Recent merges have had little time to be reverted.",
      format: (cohort) => percent(cohort.revertRate.value),
      gap: (ai, other) => gapOfShare(ai.revertRate, other.revertRate),
      metric: "Reverted in window",
    },
  ];
  const ai = byName.get("ai");
  const other = byName.get("other");
  const column = (name: AiEngineeringOutcomeCohort["cohort"], label: string, emphasis = false) => ({
    align: "right" as const,
    key: name,
    label,
    render: (row: ComparisonRow) => {
      const cohort = byName.get(name);
      const value = cohort ? row.format(cohort) : "—";
      return emphasis ? <span className="font-semibold text-foreground">{value}</span> : value;
    },
  });
  return (
    <ChartPanel
      title="AI-assisted vs other merged PRs"
      action={<StateFlag state={unavailable ? "unavailable" : "measured"} />}
    >
      <p className="mb-3 max-w-4xl text-xs leading-relaxed text-muted-foreground">
        Both cohorts are merged PRs from the same repositories and window. Medians are used because
        merge times and sizes are skewed. The gap column is AI-assisted minus other; read it as “how
        AI-assisted work looks today”, not as the effect of AI.
      </p>
      <div className="mb-2 flex flex-wrap gap-4 text-xs text-muted-foreground">
        <span className="inline-flex items-center gap-1.5">
          <span
            aria-hidden
            className="size-2.5 rounded-sm"
            style={{ backgroundColor: PALETTE.raspberryPlum }}
          />
          AI-assisted: automation author or Operators chat link
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span aria-hidden className="size-2.5 rounded-sm bg-muted-foreground/50" />
          Other: no AI authoring provenance
        </span>
      </div>
      <div className="tabular-nums">
        <DataTable
          columns={[
            {
              key: "metric",
              label: "Measure",
              render: (row) => (
                <span className="inline-flex items-center gap-1">
                  {row.metric}
                  <DefinitionTrigger definition={row.definition} label={row.metric} />
                </span>
              ),
            },
            column("ai", "AI-assisted", true),
            column("other", "Other"),
            column("all", "All merged"),
            {
              align: "right" as const,
              key: "gap",
              label: "Gap",
              render: (row) => (
                <span className="text-muted-foreground">
                  {ai && other && !unavailable ? row.gap(ai, other) : "—"}
                </span>
              ),
            },
          ]}
          rows={rows}
          rowKey={(row) => row.metric}
        />
      </div>
    </ChartPanel>
  );
}

type ReviewFlowNode = ReviewWorkflowGraphNode & {
  height: number;
  width: number;
  x: number;
  y: number;
};

function reviewFlowHeight(value: number, maximum: number): number {
  if (value === 0) return 44;
  return 44 + Math.max(0, Math.min(16, 16 * Math.sqrt(value / maximum)));
}

function reviewFlowPath(source: ReviewFlowNode, target: ReviewFlowNode): string {
  const sourceX = source.x + source.width;
  const sourceY = source.y + source.height / 2;
  const targetX = target.x;
  const targetY = target.y + target.height / 2;
  const bend = (targetX - sourceX) * 0.45;
  return `M ${sourceX} ${sourceY} C ${sourceX + bend} ${sourceY}, ${targetX - bend} ${targetY}, ${targetX} ${targetY}`;
}

function ReviewFlowNodeView(props: {
  dimmed: boolean;
  node: ReviewFlowNode;
  onHover: (id: string | null) => void;
}) {
  const { node } = props;
  const valueLabel = node.observed && node.value !== null ? node.value.toLocaleString() : "—";
  const active = node.observed && !props.dimmed;
  const fillClass =
    node.tone === "accent" && active
      ? ACCENT_FILL_CLASS
      : node.tone === "cohort" && active
        ? AI_COHORT_FILL_CLASS
        : node.tone === "attention" && active && (node.value ?? 0) > 0
          ? ATTENTION_FILL_CLASS
          : node.tone === "soft" && active && (node.value ?? 0) > 0
            ? `${ACCENT_FILL_CLASS} opacity-60`
            : "fill-muted";
  const accent = active && (node.tone === "accent" || node.tone === "cohort");
  const textClass = accent ? "fill-white" : "fill-foreground";
  const subTextClass = accent ? "fill-white" : "fill-muted-foreground";
  const emptyLabel = !node.observed
    ? "awaiting telemetry"
    : node.value === 0 && node.emptyLabel
      ? node.emptyLabel
      : null;
  return (
    <g
      aria-label={`${node.label}: ${valueLabel}`}
      className="cursor-pointer focus-visible:outline-none"
      opacity={props.dimmed ? 0.45 : 1}
      role="button"
      tabIndex={0}
      onFocus={() => props.onHover(node.id)}
      onMouseEnter={() => props.onHover(node.id)}
      onMouseLeave={() => props.onHover(null)}
      onBlur={() => props.onHover(null)}
    >
      <title>{`${node.label}: ${valueLabel} — ${node.definition}`}</title>
      <rect
        className={fillClass}
        height={node.height}
        rx="4"
        width={node.width}
        x={node.x}
        y={node.y}
      />
      <text className={textClass} fontSize="12" fontWeight="600" x={node.x + 12} y={node.y + 20}>
        {node.label}
      </text>
      <text
        className={textClass}
        fontSize="12"
        fontWeight="700"
        textAnchor="end"
        x={node.x + node.width - 12}
        y={node.y + 20}
      >
        {valueLabel}
      </text>
      {emptyLabel ? (
        <text
          className={subTextClass}
          fillOpacity={accent ? 0.85 : 1}
          fontSize="10"
          x={node.x + 12}
          y={node.y + 32}
        >
          {emptyLabel}
        </text>
      ) : null}
    </g>
  );
}

const UNAVAILABLE_WORKFLOW_METRIC: AiEngineeringMetric = {
  denominator: 0,
  numerator: 0,
  state: "unavailable",
  value: null,
};

function unavailableReviewWorkflow(
  dimensions: AiEngineeringSnapshotResponse["review"]["dimensions"],
): AiEngineeringReviewWorkflow {
  return {
    availability: "unavailable",
    dimensions: dimensions.map((dimension) => ({
      ...dimension,
      findings: 0,
      state: "unavailable",
    })),
    findingCoverage: UNAVAILABLE_WORKFLOW_METRIC,
    findingsActedOn: UNAVAILABLE_WORKFLOW_METRIC,
    reviewCoverage: UNAVAILABLE_WORKFLOW_METRIC,
  };
}

function reviewFlowPosition(
  node: ReviewWorkflowGraphNode,
  dimensionIndex: number,
): Pick<ReviewFlowNode, "width" | "x" | "y"> {
  const positions: Record<string, Pick<ReviewFlowNode, "width" | "x" | "y">> = {
    "ai-assisted": { width: 180, x: 24, y: 196 },
    "changed-after-finding": { width: 196, x: 704, y: 62 },
    "no-change-after-finding": { width: 196, x: 704, y: 178 },
    "no-review-request": { width: 190, x: 250, y: 304 },
    "no-tagged-finding": { width: 190, x: 478, y: 244 },
    "review-requested": { width: 190, x: 250, y: 126 },
    "tagged-finding": { width: 190, x: 478, y: 74 },
  };
  return positions[node.id] ?? { width: 248, x: 952, y: 32 + dimensionIndex * 66 };
}

function flowLinkClass(link: ReviewWorkflowGraphLink, target: ReviewFlowNode): string {
  if (link.kind === "finding-comment") return CONTEXT_STROKE_CLASS;
  return target.tone === "attention" ? ATTENTION_STROKE_CLASS : ACCENT_CLASS;
}

function ReviewFlow({ data }: { data: AiEngineeringSnapshotResponse }) {
  const [hovered, setHovered] = useState<string | null>(null);
  const workflow =
    data.outcomes.aiReview.workflow ?? unavailableReviewWorkflow(data.review.dimensions);
  const graph = buildReviewWorkflowGraph(workflow);
  const coreMaximum = Math.max(
    ...graph.nodes.filter((node) => !node.id.startsWith("finding-")).map((node) => node.value ?? 0),
    1,
  );
  const dimensionMaximum = Math.max(
    ...graph.nodes.filter((node) => node.id.startsWith("finding-")).map((node) => node.value ?? 0),
    1,
  );
  let dimensionIndex = 0;
  const nodes = graph.nodes.map((node): ReviewFlowNode => {
    const isDimension = node.id.startsWith("finding-");
    const position = reviewFlowPosition(node, isDimension ? dimensionIndex++ : -1);
    return {
      ...node,
      ...position,
      height: reviewFlowHeight(node.value ?? 0, isDimension ? dimensionMaximum : coreMaximum),
    };
  });
  const nodesById = new Map(nodes.map((node) => [node.id, node]));
  const selectedNode = nodes.find((node) => node.id === hovered);
  const populatedFindingNodes = nodes.filter(
    (node) => node.id.startsWith("finding-") && node.observed && (node.value ?? 0) > 0,
  );
  const taggedComments = populatedFindingNodes.reduce((sum, node) => sum + (node.value ?? 0), 0);
  const connectedNodeIds = new Set<string>(hovered ? [hovered] : nodes.map((node) => node.id));
  if (hovered) {
    for (const link of graph.links) {
      if (link.source === hovered || link.target === hovered) {
        connectedNodeIds.add(link.source);
        connectedNodeIds.add(link.target);
      }
    }
  }
  const aiAssisted = nodesById.get("ai-assisted")?.value;
  const reviewed = nodesById.get("review-requested")?.value;
  const notReviewed = nodesById.get("no-review-request")?.value;
  const withFindings = nodesById.get("tagged-finding")?.value;

  return (
    <>
      <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
        <p className="max-w-3xl text-xs leading-relaxed text-muted-foreground">
          Follow AI-assisted merged PRs through review request, structured findings, and a later
          commit. Solid paths preserve PR counts; orange paths are drop-offs. Dashed paths fan out
          tagged finding comments by dimension—a PR can emit more than one comment.
        </p>
        <span className="text-[11px] text-muted-foreground">Hover or focus a node</span>
      </div>
      <div className="overflow-x-auto rounded-md border border-border/70 bg-card p-2">
        <svg
          aria-label="AI-assisted pull request review workflow"
          className="min-w-[1230px]"
          role="img"
          viewBox="0 0 1230 460"
        >
          <text className="fill-muted-foreground" fontSize="10" fontWeight="600" x="24" y="14">
            AI-ASSISTED MERGED PRS
          </text>
          <text className="fill-muted-foreground" fontSize="10" fontWeight="600" x="250" y="14">
            REVIEW REQUEST
          </text>
          <text className="fill-muted-foreground" fontSize="10" fontWeight="600" x="478" y="14">
            STRUCTURED FINDING
          </text>
          <text className="fill-muted-foreground" fontSize="10" fontWeight="600" x="704" y="14">
            FOLLOW-UP COMMIT
          </text>
          <line className="stroke-border" x1="926" x2="926" y1="18" y2="444" />
          <text className="fill-muted-foreground" fontSize="10" fontWeight="600" x="952" y="14">
            FINDING TAGS ·{" "}
            {workflow.availability === "unavailable" ? "—" : `${taggedComments} COMMENTS`}
          </text>
          {graph.links.map((link) => {
            const source = nodesById.get(link.source);
            const target = nodesById.get(link.target);
            if (!source || !target) return null;
            const linkActive = hovered === link.source || hovered === link.target;
            const maximum = link.kind === "pr" ? coreMaximum : dimensionMaximum;
            return (
              <path
                key={link.id}
                className={flowLinkClass(link, target)}
                d={reviewFlowPath(source, target)}
                data-flow-kind={link.kind}
                fill="none"
                opacity={hovered ? (linkActive ? 0.75 : 0.06) : link.kind === "pr" ? 0.32 : 0.22}
                strokeDasharray={link.kind === "finding-comment" ? "5 5" : undefined}
                strokeLinecap="round"
                strokeWidth={Math.max(2, ((link.kind === "pr" ? 54 : 16) * link.value) / maximum)}
              />
            );
          })}
          {nodes.map((node) => (
            <ReviewFlowNodeView
              key={node.id}
              dimmed={!connectedNodeIds.has(node.id)}
              node={node}
              onHover={setHovered}
            />
          ))}
        </svg>
      </div>
      <div className="mt-3 flex min-h-7 items-center justify-between gap-3 text-xs text-muted-foreground">
        <span>
          {selectedNode
            ? `${selectedNode.label}: ${selectedNode.observed && selectedNode.value !== null ? selectedNode.value.toLocaleString() : "—"} — ${selectedNode.definition}`
            : aiAssisted === null || aiAssisted === undefined
              ? "Detailed review workflow telemetry is unavailable for this window."
              : `${reviewed?.toLocaleString() ?? "—"} requested + ${notReviewed?.toLocaleString() ?? "—"} without = ${aiAssisted.toLocaleString()} AI-assisted merged PRs`}
        </span>
        <span className="shrink-0 font-medium text-foreground">
          {withFindings?.toLocaleString() ?? "—"} PRs with findings ·{" "}
          {taggedComments.toLocaleString()} tagged comments
        </span>
      </div>
    </>
  );
}

function StatStrip(props: {
  items: Array<{ definition?: string; label: string; value: string }>;
  size?: "md" | "sm";
  columns: string;
}) {
  return (
    <div className={`grid gap-3 ${props.columns}`}>
      {props.items.map((item) => (
        <div key={item.label} className="border-l-2 border-border pl-2.5">
          <p
            className={`font-semibold tracking-tight text-foreground ${props.size === "sm" ? "text-base" : "text-xl"}`}
          >
            {item.value}
          </p>
          <p className="inline-flex items-center gap-1 text-[11px] leading-snug text-muted-foreground">
            {item.label}
            {item.definition ? (
              <DefinitionTrigger definition={item.definition} label={item.label} />
            ) : null}
          </p>
        </div>
      ))}
    </div>
  );
}

function ReviewPanel({ data }: { data: AiEngineeringSnapshotResponse }) {
  const outcomesUnavailable = data.outcomes.availability === "unavailable";
  const { aiReview } = data.outcomes;
  const effectiveness = [
    {
      definition: "Merged PRs in the window with an enqueued AI review or a tagged AI finding.",
      label: "Merged PRs AI-reviewed",
      value: outcomesUnavailable ? "—" : aiReview.mergedPrsReviewed.toLocaleString(),
    },
    {
      definition: "Merged PRs with at least one AI review comment carrying a finding marker.",
      label: "Merged PRs with findings",
      value: outcomesUnavailable ? "—" : aiReview.mergedPrsWithFindings.toLocaleString(),
    },
    {
      definition:
        "Merged PRs with findings that received a commit after the first finding. A proxy for the finding being acted on.",
      label: "Findings acted on",
      value: percent(aiReview.changedAfterFinding.value),
    },
    {
      definition:
        "Share of AI-reviewed merged PRs where a human later requested changes, next to the same share for merged PRs without AI review. If AI review catches real issues first, the left figure should be lower.",
      label: "Human changes requested · AI-reviewed vs other",
      value: `${percent(aiReview.changesRequestedOnReviewedPrs.value)} vs ${percent(aiReview.changesRequestedOnOtherPrs.value)}`,
    },
    {
      definition:
        "Median human comments on AI-reviewed merged PRs next to merged PRs without AI review. A reviewer-burden comparison.",
      label: "Median human comments · AI-reviewed vs other",
      value: `${plain(aiReview.medianHumanCommentsOnReviewedPrs, 1)} vs ${plain(aiReview.medianHumanCommentsOnOtherPrs, 1)}`,
    },
  ];

  return (
    <ChartPanel
      title="Is AI review working?"
      action={
        <Popover>
          <PopoverTrigger
            type="button"
            aria-label="About AI review"
            className="inline-flex size-5 items-center justify-center rounded-full text-muted-foreground hover:text-foreground"
          >
            <InfoIcon className="size-4" aria-hidden />
          </PopoverTrigger>
          <PopoverPopup side="left" tooltipStyle className="max-w-80 text-xs leading-relaxed">
            This panel separates observed review activity from review quality. Precision, recall,
            and severity-weighted scores require adjudicated reference labels.
          </PopoverPopup>
        </Popover>
      }
    >
      <StatStrip columns="grid-cols-1 sm:grid-cols-2 lg:grid-cols-5" items={effectiveness} />
      <p className="mt-3 mb-5 max-w-4xl text-xs leading-relaxed text-muted-foreground">
        “Acted on” and the AI-reviewed versus other comparison are the closest available answers to
        whether AI review finds things worth fixing. They are outcome proxies from GitHub commits
        and reviews; precision and recall still need human adjudication.
      </p>
      <ReviewFlow data={data} />
    </ChartPanel>
  );
}

function EvaluationsPanel({ data }: { data: AiEngineeringSnapshotResponse }) {
  const { evaluations } = data;
  const configured = evaluations.availability === "live";
  const reportPerformance = configured && evaluations.calibrated;
  const items = [
    {
      definition:
        "PASS judgments divided by PASS, PARTIAL, and FAIL judgments. UNKNOWN is shown separately and excluded from the denominator.",
      label: "Task compliance pass rate",
      value: reportPerformance ? percent(evaluations.passRate.value) : "—",
    },
    {
      definition:
        "Scored sessions divided by sessions selected by the deterministic sampling policy.",
      label: "Evaluation coverage",
      value: percent(evaluations.coverage.value),
    },
    {
      definition:
        "Archived coding sessions with both a completed turn and an observed file change.",
      label: "Eligible coding sessions",
      value: configured ? evaluations.eligibleRuns.toLocaleString() : "—",
    },
    {
      definition: "Sampled sessions that received a categorical task-compliance judgment.",
      label: "Scored sessions",
      value: configured ? evaluations.scoredRuns.toLocaleString() : "—",
    },
  ];
  const labels = [
    { label: "Pass", value: evaluations.labels.pass },
    { label: "Partial", value: evaluations.labels.partial },
    { label: "Fail", value: evaluations.labels.fail },
    { label: "Unknown", value: evaluations.labels.unknown },
  ];

  return (
    <ChartPanel title="Task compliance scoring">
      <p className="mb-3 max-w-4xl text-xs leading-relaxed text-muted-foreground">
        A sampled LLM judge evaluates archived coding-agent sessions against the user requests in
        their persisted trace. Scores are diagnostic evidence, not ground truth or personnel
        rankings. Rubric {evaluations.rubricVersions.join(", ") || "not observed"}; judge models{" "}
        {evaluations.judgeModels.join(", ") || "not observed"}.
      </p>
      {!configured ? (
        <p className="mb-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs leading-relaxed text-amber-900 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-200">
          Evaluation credentials are unavailable. No task-compliance result is being reported.
        </p>
      ) : !evaluations.calibrated ? (
        <p className="mb-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs leading-relaxed text-amber-900 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-200">
          Scores are being collected, but performance results remain hidden until the judge is
          calibrated against human labels.
        </p>
      ) : evaluations.scoredRuns === 0 ? (
        <p className="mb-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs leading-relaxed text-amber-900 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-200">
          The evaluation pipeline is configured, but no sampled coding sessions have been scored in
          this window yet.
        </p>
      ) : null}
      <StatStrip columns="grid-cols-2 lg:grid-cols-4" items={items} />
      <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
        {labels.map((item) => (
          <div
            key={item.label}
            className="rounded-md border border-border/70 bg-muted/20 px-3 py-2"
          >
            <p className="text-lg font-semibold tabular-nums text-foreground">
              {reportPerformance ? item.value.toLocaleString() : "—"}
            </p>
            <p className="text-[11px] text-muted-foreground">{item.label}</p>
          </div>
        ))}
      </div>
      {configured && evaluations.failedRuns > 0 ? (
        <p className="mt-3 text-xs text-muted-foreground">
          {evaluations.failedRuns.toLocaleString()} evaluation pipeline{" "}
          {evaluations.failedRuns === 1 ? "failure" : "failures"} need attention.
        </p>
      ) : null}
    </ChartPanel>
  );
}

function InstrumentHealthPanel({ data }: { data: AiEngineeringSnapshotResponse }) {
  const operatorMetricState: AiEngineeringMetricState =
    data.freshness.operators === "live" ? "measured" : "unavailable";
  const githubCommentState: AiEngineeringMetricState =
    data.freshness.operators === "unavailable" || data.freshness.github === "unavailable"
      ? "unavailable"
      : "measured";
  const activity = [
    { label: "PRs reviewed", value: observedCount(data.review.prCount, operatorMetricState) },
    {
      label: "Review requests",
      value: observedCount(data.review.reviewRequests, operatorMetricState),
    },
    { label: "AI comments", value: observedCount(data.review.aiComments, githubCommentState) },
    {
      label: "Human comments",
      value: observedCount(data.review.humanComments, githubCommentState),
    },
    {
      label: "Review sessions",
      value: observedCount(data.review.reviewSessions, operatorMetricState),
    },
    {
      label: "Completed turns",
      value: observedCount(data.review.completedTurns, operatorMetricState),
    },
  ];
  return (
    <ChartPanel title="Instrument health">
      <p className="mb-3 max-w-4xl text-xs leading-relaxed text-muted-foreground">
        Flow proxies about the tooling itself: whether turns end cleanly, whether the review
        automation fires, whether in-session checks pass, and how dense sessions are. Useful for
        spotting a broken integration; not evidence of productivity or quality on their own.
      </p>
      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
        {instrumentSignals(data).map((signal) => (
          <SignalCard
            key={signal.key}
            compact
            period={data.window.label}
            points={data.trends}
            signal={signal}
          />
        ))}
      </div>
      <p className="mt-4 mb-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
        Review activity
      </p>
      <StatStrip columns="grid-cols-2 sm:grid-cols-3 lg:grid-cols-6" items={activity} size="sm" />
    </ChartPanel>
  );
}

function StandardsAndSkillsPanel({ data }: { data: AiEngineeringSnapshotResponse }) {
  const hasStandardTelemetry = data.standards.some(
    (row) => row.pass > 0 || row.fail > 0 || row.unknown > 0,
  );
  return (
    <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
      <ChartPanel title="Observed validation commands">
        <p className="mb-3 text-xs leading-relaxed text-muted-foreground">
          Command outcomes are matched to formatting, lint, typecheck, test, build, and security
          checks. Unknown outcomes remain visible, but are excluded from the pass-rate denominator.
        </p>
        {!hasStandardTelemetry ? (
          <p className="mb-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs leading-relaxed text-amber-900 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-200">
            No command-completion telemetry was emitted in this window. The dashes below mean “not
            observed,” not zero passing checks.
          </p>
        ) : null}
        <div className="tabular-nums">
          <DataTable
            columns={[
              { key: "label", label: "Standard", sortable: true },
              {
                key: "pass",
                label: "Pass",
                align: "right",
                sortable: true,
                render: (row) =>
                  row.pass + row.fail + row.unknown === 0 ? "—" : row.pass.toLocaleString(),
              },
              {
                key: "fail",
                label: "Fail",
                align: "right",
                sortable: true,
                render: (row) =>
                  row.pass + row.fail + row.unknown === 0 ? "—" : row.fail.toLocaleString(),
              },
              {
                key: "unknown",
                label: "Unknown",
                align: "right",
                sortable: true,
                render: (row) =>
                  row.pass + row.fail + row.unknown === 0 ? "—" : row.unknown.toLocaleString(),
              },
              {
                key: "passRate",
                label: "Pass rate",
                align: "right",
                sortable: true,
                render: (row) => percent(row.passRate),
                sortValue: (row) => row.passRate,
              },
            ]}
            rows={data.standards}
            rowKey={(row) => row.label}
            defaultSort={{ key: "passRate", direction: "desc" }}
          />
        </div>
      </ChartPanel>

      <ChartPanel title="Observed skill reads">
        <p className="mb-3 text-xs leading-relaxed text-muted-foreground">
          Skills are counted when a persisted command reads a versioned SKILL.md path. This is
          direct telemetry, not an inference from the final diff; completion is currently the
          command outcome proxy.
        </p>
        <div className="tabular-nums">
          <DataTable
            columns={[
              { key: "skill", label: "Skill", sortable: true },
              { key: "invocations", label: "Reads", align: "right", sortable: true },
              { key: "completed", label: "Successful reads", align: "right", sortable: true },
              {
                key: "adherenceRate",
                label: "Read success",
                align: "right",
                sortable: true,
                render: (row) => percent(row.adherenceRate),
                sortValue: (row) => row.adherenceRate,
              },
            ]}
            rows={data.skills}
            rowKey={(row) => row.skill}
            emptyMessage="No skill-file reads observed in this window."
            defaultSort={{ key: "invocations", direction: "desc" }}
          />
        </div>
      </ChartPanel>
    </div>
  );
}

function UsagePanel({ data }: { data: AiEngineeringSnapshotResponse }) {
  const operatorMetricState: AiEngineeringMetricState =
    data.freshness.operators === "live" ? "measured" : "unavailable";
  const unavailable = operatorMetricState === "unavailable";
  const completedTurns = data.review.completedTurns;
  const ratio = (numerator: number, denominator: number): number | null =>
    unavailable || denominator === 0 ? null : numerator / denominator;
  const tokensPerTurn = ratio(data.usage.totalTokens, completedTurns);
  const outputShare = ratio(data.usage.outputTokens, data.usage.totalTokens);

  const leading = [
    {
      definition: "Recorded inference cost divided by AI-assisted PRs merged in the window.",
      label: "Cost per AI-assisted merge",
      value: currency(data.outcomes.costPerAiMergedPr),
    },
    {
      definition:
        "Recorded inference cost divided by Linear tickets that reached “Deployed to production” in the window. Portfolio-level: all AI cost over all deploys, not only AI-assisted ones.",
      label: "Cost per deployed ticket",
      value: currency(data.outcomes.costPerDeployedTicket),
    },
    {
      definition:
        "Cost recorded on usage events. Shown only when a cost was recorded; it is not reconstructed from model names or token counts.",
      label: "Recorded inference cost",
      value: currency(data.usage.costUsd),
    },
  ];
  const inputs = [
    {
      definition: "Sum of persisted session usage events in the window, all token types.",
      label: "Total tokens",
      value: unavailable ? "—" : compactNumber(data.usage.totalTokens),
    },
    {
      definition: "Sum of output tokens from persisted session usage events.",
      label: "Output tokens",
      value: unavailable ? "—" : compactNumber(data.usage.outputTokens),
    },
    {
      definition: "Total tokens divided by completed AI turns in the window.",
      label: "Tokens per completed turn",
      value: tokensPerTurn == null ? "—" : compactNumber(tokensPerTurn),
    },
    {
      definition:
        "Output tokens divided by total tokens. A low share usually means large context or cache reads dominate.",
      label: "Output share",
      value: percent(outputShare),
    },
  ];
  return (
    <ChartPanel title="Cost per shipped change">
      <StatStrip columns="grid-cols-1 sm:grid-cols-3" items={leading} />
      <p className="mt-3 mb-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
        Volume inputs
      </p>
      <StatStrip columns="grid-cols-2 sm:grid-cols-4" items={inputs} size="sm" />
      <p className="mt-3 text-xs leading-relaxed text-muted-foreground">
        Usage totals come from persisted session usage events. Cost per shipped change is the
        efficiency figure that matters; token volume is an input, not an outcome.
      </p>
    </ChartPanel>
  );
}

type BreakoutKind = "engineer" | "repo";

function BreakoutPanel({ data }: { data: AiEngineeringSnapshotResponse }) {
  const [kind, setKind] = useState<BreakoutKind>("engineer");
  const portfolio = data.breakouts.filter((row) => row.kind === "all");
  const rows = [...portfolio, ...data.breakouts.filter((row) => row.kind === kind)];
  const columns = [
    {
      key: "cohort",
      label: kind === "engineer" ? "Engineer" : "Repository",
      sortable: true,
      render: (row: AiEngineeringBreakoutRow) =>
        row.kind === "all" ? <strong>{row.cohort}</strong> : row.cohort,
    },
    { key: "turnCount", label: "Turns", align: "right" as const, sortable: true },
    { key: "sessionCount", label: "Sessions", align: "right" as const, sortable: true },
    {
      key: "prCount",
      label: "AI-associated PRs",
      align: "right" as const,
      sortable: true,
      definition:
        "Distinct PRs with observed Operators session provenance or an enqueued AI review receipt. This is not every PR authored by an engineer.",
    },
    ...(kind === "engineer"
      ? [
          {
            key: "authoredPrsOpened",
            label: "GitHub PRs opened",
            align: "right" as const,
            sortable: true,
            definition:
              "PRs opened by this engineer or attributed to them through Operators metadata in the window. Activity context, not AI attribution.",
            render: (row: AiEngineeringBreakoutRow) =>
              row.authoredPrsOpened?.toLocaleString() ?? "—",
            sortValue: (row: AiEngineeringBreakoutRow) => row.authoredPrsOpened,
          },
        ]
      : [
          {
            key: "aiComments",
            label: "AI comments",
            align: "right" as const,
            sortable: true,
            definition:
              "Comments by the Operators automation identity on tracked PRs in this repository.",
          },
        ]),
    {
      key: "standardsPassRate",
      label: "Standards pass",
      align: "right" as const,
      sortable: true,
      render: (row: AiEngineeringBreakoutRow) => percent(row.standardsPassRate),
      sortValue: (row: AiEngineeringBreakoutRow) => row.standardsPassRate,
    },
    { key: "skillInvocations", label: "Skill reads", align: "right" as const, sortable: true },
  ];
  return (
    <ChartPanel
      title="Where AI is used"
      action={
        <div className="flex flex-wrap gap-1.5" role="group" aria-label="Breakout dimension">
          <FilterToggle active={kind === "engineer"} onClick={() => setKind("engineer")}>
            Engineers
          </FilterToggle>
          <FilterToggle active={kind === "repo"} onClick={() => setKind("repo")}>
            Repositories
          </FilterToggle>
        </div>
      }
    >
      <p className="mb-3 max-w-4xl text-xs leading-relaxed text-muted-foreground">
        Usage context by engineer and repository, using the same dimensions as Eng Velocity. This is
        where AI is being used, not how well; it is not a ranking. The portfolio row is the whole
        window, not a sum of the rows below it.{" "}
        {kind === "engineer"
          ? "AI review comments are attributed to repositories, not engineers, so they appear in the repository view."
          : "GitHub PRs opened is an engineer measure and is shown in the engineer view."}
      </p>
      <div className="tabular-nums">
        <DataTable
          columns={columns}
          rows={rows}
          rowKey={(row) => `${row.kind}:${row.cohort}`}
          emptyMessage="No AI session or PR provenance observed in this window."
          defaultSort={{ key: "cohort", direction: "asc" }}
        />
      </div>
    </ChartPanel>
  );
}

function TelemetryNotesPanel({ data }: { data: AiEngineeringSnapshotResponse }) {
  const rows: Array<{
    evidence: string;
    signal: string;
    state: AiEngineeringMetricState;
  }> = [
    {
      signal: "AI authoring provenance",
      evidence: "Automation identity as PR author, or the Operators chat link in the PR body",
      state: data.outcomes.availability === "live" ? "measured" : "unavailable",
    },
    {
      signal: "PR outcomes",
      evidence: "GitHub PR reviews, commits, merge times, and revert PRs",
      state: data.outcomes.availability === "live" ? "measured" : "unavailable",
    },
    {
      signal: "Deploys",
      evidence: "Linear tickets reaching “Deployed to production”",
      state: data.outcomes.deployedTickets === null ? "unavailable" : "measured",
    },
    {
      signal: "AI session activity",
      evidence: "Session events joined to users and GitHub PR threads",
      state: data.freshness.operators === "live" ? "measured" : ("unavailable" as const),
    },
    {
      signal: "PR review activity",
      evidence: "Review receipts plus GitHub comments",
      state: data.freshness.github === "unavailable" ? "unavailable" : "measured",
    },
    {
      signal: "Review precision & recall",
      evidence: "Human-adjudicated finding labels and a reference defect set",
      state: "insufficient_data" as const,
    },
    {
      signal: "Task compliance scoring",
      evidence: "Versioned rubric judgments over sampled, archived coding-session traces",
      state:
        data.evaluations.availability === "unavailable"
          ? "unavailable"
          : data.evaluations.calibrated && data.evaluations.scoredRuns > 0
            ? "measured"
            : "insufficient_data",
    },
    {
      signal: "Standards checks",
      evidence: "Persisted command completion status",
      state: data.summary.standardsCompliance.state,
    },
    {
      signal: "Skill usage",
      evidence: "Persisted reads of versioned SKILL.md paths",
      state: data.summary.skillAdherence.state,
    },
    {
      signal: "Escaped defects and incidents",
      evidence: "Production defects and incidents linked to a change",
      state: "insufficient_data" as const,
    },
  ];
  return (
    <ChartPanel title="Telemetry coverage">
      <p className="mb-3 max-w-4xl text-xs leading-relaxed text-muted-foreground">
        What each signal is currently backed by. “Insufficient data” means the event contract is not
        emitted yet; it is a coverage boundary, not a low score.
      </p>
      <DataTable
        columns={[
          { key: "signal", label: "Signal", sortable: true },
          { key: "evidence", label: "Current evidence", sortable: true },
          {
            key: "state",
            label: "Status",
            sortable: true,
            render: (row) => <StatePill state={row.state} />,
            sortValue: (row) => metricStateLabel(row.state),
          },
        ]}
        rows={rows}
        rowKey={(row) => row.signal}
      />
    </ChartPanel>
  );
}

export function AiEngineeringPage(props: { data?: AiEngineeringSnapshotResponse }) {
  const data = props.data ?? createUnavailableAiEngineeringSnapshot();
  return (
    <div className="flex flex-col gap-3">
      <PageHeader data={data} />

      <SectionLabel description="Adoption, throughput, review effect, and cost. The answers.">
        Outcomes
      </SectionLabel>
      <HeadlineRow data={data} />
      <ProductivityPanel data={data} />
      <ReviewPanel data={data} />
      <UsagePanel data={data} />

      <SectionLabel description="Sampled, versioned judgments over completed coding-agent work.">
        Agent evaluations
      </SectionLabel>
      <EvaluationsPanel data={data} />

      <SectionLabel description="Tooling health and usage context. Read these to explain the outcomes, not to judge them.">
        Diagnostics
      </SectionLabel>
      <InstrumentHealthPanel data={data} />
      <StandardsAndSkillsPanel data={data} />
      <BreakoutPanel data={data} />
      <TelemetryNotesPanel data={data} />

      <p className="text-xs text-muted-foreground">
        Definitions, formulas, evidence requirements, limitations, and literature references are in
        the{" "}
        <a
          className="underline underline-offset-2 hover:text-foreground"
          href={METHODS_DOC_URL}
          rel="noreferrer"
          target="_blank"
        >
          AI engineering methods reference
        </a>
        .
      </p>
    </div>
  );
}
